import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShoppingCart, FiStar, FiEye, FiLoader } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const products = [
  { id: 1, name: 'LumenBlast Pro 1000', category: 'Lights', price: 89.99, rating: 4.9, img: 'https://images.unsplash.com/photo-1544621183-5fc55e581e6a?auto=format&fit=crop&w=600&q=80', badge: 'Elite' },
  { id: 2, name: 'Titan U-Lock Heavy', category: 'Locks', price: 110.00, rating: 4.8, img: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=600&q=80', badge: 'Indestructible' },
  { id: 3, name: 'AeroCommute 25L', category: 'Bags', price: 145.00, rating: 5.0, img: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?auto=format&fit=crop&w=600&q=80', badge: 'New Gen' },
  { id: 4, name: 'CityBlink Rear Guard', category: 'Lights', price: 34.99, rating: 4.6, img: 'https://images.unsplash.com/photo-1485965120184-e220f721d03e?auto=format&fit=crop&w=600&q=80' },
  { id: 5, name: 'FlexCable Urban', category: 'Locks', price: 45.00, rating: 4.5, img: 'https://images.unsplash.com/photo-1628151015968-3a4429e9ef04?auto=format&fit=crop&w=600&q=80' },
  { id: 6, name: 'Messenger DryLite', category: 'Bags', price: 95.00, rating: 4.7, img: 'https://images.unsplash.com/photo-1547949003-9792a18a2601?auto=format&fit=crop&w=600&q=80' },
];

const ProductGrid = ({ onAddToCart }) => {
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [addingId, setAddingId] = useState(null);

  useEffect(() => {
    // Simulate initial loading
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  const handleAdd = (product) => {
    setAddingId(product.id);
    setTimeout(() => {
      onAddToCart(product);
      setAddingId(null);
    }, 600);
  };

  const filtered = filter === 'All' ? products : products.filter(p => p.category === filter);

  return (
    <section id="shop" className="py-32 bg-asphalt-dark">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-end gap-8 mb-20">
          <div className="max-w-2xl">
            <div className="flex items-center gap-4 mb-4">
              <div className="h-[2px] w-12 bg-signal"></div>
              <span className="text-signal font-black text-xs tracking-[0.4em] uppercase">Inventory Arsenal</span>
            </div>
            <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter">
              Street <span className="text-stroke">Tech.</span>
            </h2>
          </div>

          <div className="flex bg-asphalt-light p-1 rounded-sm overflow-x-auto max-w-full">
            {['All', 'Lights', 'Locks', 'Bags'].map(cat => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-6 py-3 text-[10px] whitespace-nowrap font-black uppercase tracking-widest transition-all ${
                  filter === cat ? 'bg-signal text-white shadow-lg' : 'text-gray-500 hover:text-white'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/5] bg-asphalt rounded-sm mb-6"></div>
                <div className="h-4 bg-asphalt w-1/4 mb-4"></div>
                <div className="h-8 bg-asphalt w-3/4 mb-4"></div>
                <div className="h-10 bg-asphalt w-1/2"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            <AnimatePresence mode="popLayout">
              {filtered.length === 0 ? (
                <div className="col-span-full py-20 text-center">
                  <p className="text-gray-500 font-black uppercase tracking-widest">No matching gear found in current sector</p>
                </div>
              ) : (
                filtered.map((product) => (
                  <motion.div
                    layout
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="group relative"
                  >
                    <div className="relative aspect-[4/5] bg-asphalt overflow-hidden border border-white/5 group-hover:border-signal/30 transition-all duration-500">
                      {product.badge && (
                        <div className="absolute top-6 left-6 z-10 bg-signal text-white text-[9px] font-black uppercase tracking-widest py-1 px-3">
                          {product.badge}
                        </div>
                      )}
                      <img 
                        src={product.img} 
                        alt={product.name}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700 opacity-60 group-hover:opacity-100" 
                      />
                      
                      <div className="absolute inset-0 bg-asphalt-dark/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-4">
                        <button 
                          onClick={() => handleAdd(product)}
                          disabled={addingId === product.id}
                          className="w-12 h-12 bg-white text-asphalt-dark flex items-center justify-center hover:bg-signal hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 disabled:bg-gray-400"
                          aria-label={`Add ${product.name} to cart`}
                        >
                          {addingId === product.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiShoppingCart} />}
                        </button>
                        <button className="w-12 h-12 bg-white/10 text-white backdrop-blur-md flex items-center justify-center hover:bg-white hover:text-asphalt-dark transition-all transform translate-y-4 group-hover:translate-y-0 delay-75" aria-label="View details">
                          <SafeIcon icon={FiEye} />
                        </button>
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500">{product.category}</span>
                        <div className="flex items-center gap-1 text-signal text-xs font-black">
                          <SafeIcon icon={FiStar} className="fill-current" /> {product.rating}
                        </div>
                      </div>
                      <h3 className="text-xl font-black uppercase tracking-tighter text-white group-hover:text-signal transition-colors">{product.name}</h3>
                      <p className="text-2xl font-black text-white mt-2">${product.price.toFixed(2)}</p>
                    </div>
                  </motion.div>
                ))
              )}
            </AnimatePresence>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductGrid;